/**
 * Форматирует число, разделяя разряды пробелами для улучшения читаемости.
 *
 * @param value - Число для форматирования
 * @returns Отформатированную строку числа с пробелами в качестве разделителей разрядов
 *
 * @example
 * formatNumberWithSpaces(1000) // "1 000"
 * formatNumberWithSpaces(1234567) // "1 234 567"
 * formatNumberWithSpaces(123.45) // "123.45"
 */
export const formatNumberWithSpaces = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return '0';
  }

  // Используем стандартный Intl.NumberFormat для форматирования
  return new Intl.NumberFormat('ru-RU', {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/**
 * Форматирует число как валюту в рублях с разделением разрядов.
 *
 * @param value - Число для форматирования
 * @returns Отформатированную строку числа с символом рубля
 *
 * @example
 * formatCurrency(1000) // "1 000 ₽"
 * formatCurrency(1234567) // "1 234 567 ₽"
 */
export const formatCurrency = (value: number | string | null | undefined): string => {
  const formattedNumber = formatNumberWithSpaces(value);
  return `${formattedNumber} ₽`;
};

/**
 * Убирает форматирование из строки и возвращает чистое число.
 *
 * @param value - Отформатированная строка числа
 * @returns Числовое значение
 *
 * @example
 * parseFormattedNumber("1 000") // 1000
 * parseFormattedNumber("1 234 567") // 1234567
 */
export const parseFormattedNumber = (value: string): number => {
  if (!value || value.trim() === '') {
    return 0;
  }

  // Убираем все пробелы и парсим число
  const cleanValue = value.replace(/\s/g, '');
  const numValue = parseFloat(cleanValue);

  return isNaN(numValue) ? 0 : numValue;
};

/**
 * Форматирует значение для поля ввода с разделением разрядов.
 *
 * @param value - Значение для форматирования
 * @returns Отформатированную строку для отображения в поле ввода
 *
 * @example
 * formatInputNumber("1000") // "1 000"
 * formatInputNumber("1234567") // "1 234 567"
 * formatInputNumber("0") // "0"
 */
export const formatInputNumber = (value: string | number): string => {
  if (!value && value !== 0) {
    return '';
  }

  const numValue = typeof value === 'string' ? parseFormattedNumber(value) : value;

  if (numValue === 0) {
    return '0';
  }

  // Для целых чисел убираем дробную часть из форматирования
  return new Intl.NumberFormat('ru-RU', {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: numValue % 1 === 0 ? 0 : 2,
  }).format(numValue);
};
